import { useState, useEffect } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { Box, CircularProgress, Typography, useTheme, useMediaQuery } from '@mui/material';
import { tokens } from '../../theme';
import axios from 'axios';

// Color palette for 6 categories
const decPieColors = [
  '#f47560', // DAO - Red/Orange
  '#e8a838', // Bridges - Gold
  '#ff6b6b', // Splinterlands - Red
  '#9c27b0', // Land LP - Purple
  '#61cdbb', // Off-Chain - Teal
  '#97e3d5', // Other - Light Teal
];

const DecDistributionChart = ({ onDisclaimer }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Custom theme for the pie chart
  const customChartTheme = {
    tooltip: {
      container: {
        background: colors.primary[700],
        color: colors.grey[100],
        fontSize: isMobile ? 10 : 12,
      }
    }
  };

  // Format large numbers
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  // Fetch data from new DEC data API
  const fetchDistributionData = async () => {
    try {
      setLoading(true);
      
      // Fetch from DEC data endpoint ONLY
      const decDataResponse = await axios.get('https://ecapi.splinterlands.com/players/dec_data');
      
      if (decDataResponse.data) {
        const decData = decDataResponse.data.supply;
        
        // 1. DAO Reserves (use reserves.total for complete DAO holdings)
        const daoBalance = Number(decData.reserves?.['total'] || 0);
        
        // 2. Splinterlands Company (market escrow only from API)
        const companyBalance = Number(decData.markets_escrow?.['$SL-MARKET'] || 0);
        
        // 3. Land LP
        const landLP = Number(decData.land_lps?.total || 0);
        
        // 4. Off-Chain (ETH + BSC + Hive Engine)
        const offChainETH = Number(decData.eth_chain?.circulating || 0);
        const offChainBSC = Number(decData.bsc_chain?.circulating || 0);
        const offChainHE = Number(decData.he_chain?.circulating || 0);
        const offChainTotal = offChainETH + offChainBSC + offChainHE;
        
        // 5. Player Wallets (remaining = circulating supply - off-chain - company - land)
        const totalSupply = Number(decData.total_supply || 0);
        const playerWallets = totalSupply - daoBalance - companyBalance - landLP - offChainTotal;
        
        // Calculate total for pie chart (using total_supply as base)
        const totalForChart = totalSupply;
        
        // Prepare chart data (5 categories now: DAO, Splinterlands, Land LP, Off-Chain, Players)
        const distribution = [
          {
            id: 'DAO',
            label: `DAO Reserve (${((daoBalance / totalForChart) * 100).toFixed(1)}%)`,
            value: daoBalance,
            percentage: ((daoBalance / totalForChart) * 100).toFixed(1),
            color: decPieColors[0],
            description: `${formatNumber(daoBalance)} DEC held in DAO reserves (includes bridges)`,
          },
          {
            id: 'Splinterlands',
            label: `Splinterlands (${((companyBalance / totalForChart) * 100).toFixed(1)}%)`,
            value: companyBalance,
            percentage: ((companyBalance / totalForChart) * 100).toFixed(1),
            color: decPieColors[2],
            description: `${formatNumber(companyBalance)} DEC in market escrow`,
          },
          {
            id: 'Land LP',
            label: `Land LP (${((landLP / totalForChart) * 100).toFixed(1)}%)`,
            value: landLP,
            percentage: ((landLP / totalForChart) * 100).toFixed(1),
            color: decPieColors[3],
            description: `${formatNumber(landLP)} DEC in Land liquidity pools and staking`,
          },
          {
            id: 'Off-Chain',
            label: `Off-Chain (${((offChainTotal / totalForChart) * 100).toFixed(1)}%)`,
            value: offChainTotal,
            percentage: ((offChainTotal / totalForChart) * 100).toFixed(1),
            color: decPieColors[4],
            description: `${formatNumber(offChainTotal)} DEC on other chains (ETH: ${formatNumber(offChainETH)}, BSC: ${formatNumber(offChainBSC)}, HE: ${formatNumber(offChainHE)})`,
          },
          {
            id: 'Player Wallets',
            label: `Player Wallets (${((playerWallets / totalForChart) * 100).toFixed(1)}%)`,
            value: playerWallets,
            percentage: ((playerWallets / totalForChart) * 100).toFixed(1),
            color: decPieColors[5],
            description: `${formatNumber(playerWallets)} DEC in player wallets and other holdings`,
          }
        ];
        
        setChartData(distribution);
        
        // Disclaimer text
        const disclaimerText = `DEC Distribution (${formatNumber(totalForChart)} total supply): DAO (${formatNumber(daoBalance)}), Splinterlands (${formatNumber(companyBalance)}), Land LP (${formatNumber(landLP)}), Off-Chain (${formatNumber(offChainTotal)}), Player Wallets (${formatNumber(playerWallets)}). Data from Splinterlands API.`;
        
        if (onDisclaimer) {
          onDisclaimer(disclaimerText);
        }
        
        setError(null);
      } else {
        throw new Error('No distribution data available');
      }
      
    } catch (err) {
      console.error('Failed to fetch DEC distribution:', err);
      setError('Unable to load DEC distribution data');
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDistributionData();
    
    // Refresh every 2 minutes (data updates frequently)
    const interval = setInterval(fetchDistributionData, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Compact number format for chart labels
  const formatCompactDEC = (value) => {
    if (value >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + 'B';
    if (value >= 1_000_000) return (value / 1_000_000).toFixed(0) + 'M';
    if (value >= 1_000) return (value / 1_000).toFixed(0) + 'K';
    return value.toString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error" gutterBottom>{error}</Typography>
        <Typography variant="caption" color={colors.grey[400]}>Please try again later</Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" position="relative" display="flex" flexDirection="column">
      {/* Pie Chart */}
      <Box sx={{ flex: '1 1 0', minHeight: isMobile ? 200 : 300, width: '100%' }}>
        <ResponsivePie
          data={chartData}
          margin={isMobile ? 
            { top: 20, right: 20, bottom: 40, left: 20 } : 
            { top: 30, right: 30, bottom: 60, left: 30 }
          }
          innerRadius={isMobile ? 0.5 : 0.45}
          padAngle={0.7}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          borderWidth={1}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.2]]
          }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor={colors.grey[100]}
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor={{
            from: 'color',
            modifiers: [['darker', 2]]
          }}
          arcLabel={d => d.data.percentage > 5 ? formatCompactDEC(d.value) : ''}
          arcLabelsRadiusOffset={0.5}
          enableArcLabels={!isMobile}
          colors={{ datum: 'data.color' }}
          theme={customChartTheme}
          tooltip={({ datum }) => (
            <Box
              sx={{
                padding: isMobile ? '8px 12px' : '12px 16px',
                backgroundColor: colors.primary[700],
                borderRadius: '4px',
                boxShadow: '0px 2px 10px rgba(0,0,0,0.25)',
                border: `1px solid ${datum.color}`,
                maxWidth: isMobile ? '220px' : '280px'
              }}
            >
              <Typography 
                variant={isMobile ? "body2" : "subtitle2"} 
                sx={{ color: datum.color, fontWeight: 'bold' }}
              >
                {datum.label}
              </Typography>
              <Typography 
                variant={isMobile ? "body2" : "body1"} 
                sx={{ color: colors.grey[100], fontWeight: 'bold', mt: 0.5 }}
              >
                {datum.data.percentage}% ({formatNumber(datum.value)} DEC)
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: colors.grey[300], 
                  display: 'block', 
                  mt: 0.5,
                  fontSize: isMobile ? '0.65rem' : '0.75rem'
                }}
              >
                {datum.data.description}
              </Typography>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default DecDistributionChart;
